import { type ImageProps } from "next/image"
import { useMediaQuery } from "@oefr/ui"
import { type Maybe } from "@oefr/utils/types"
import { getStoryblokImageProps } from "../../utils/getStoryblokImageProps"

type StoryblokPictureProps = {
    src: string
    tabletSrc?: Maybe<string>
    mobileSrc?: Maybe<string>
} & Omit<ImageProps, "src">

/**
 * A component to render a responsive image using the picture element with next/image.
 * See more about art direction : https://nextjs.org/docs/app/api-reference/components/image#art-direction
 */
export function StoryblokPicture({ src, tabletSrc, mobileSrc, ...rest }: StoryblokPictureProps) {
    const desktopMediaQuery = useMediaQuery({
        breakpoint: "md",
        type: "min-width",
        queryType: null,
    })
    const tabletMediaQuery = useMediaQuery({ breakpoint: "sm", type: "min-width", queryType: null })
    const { srcSet: desktopSrcSet, ...restImage } = getStoryblokImageProps({ src, ...rest })
    const tabletProps = tabletSrc ? getStoryblokImageProps({ src: tabletSrc, ...rest }) : undefined
    const mobileProps = mobileSrc ? getStoryblokImageProps({ src: mobileSrc, ...rest }) : undefined

    return (
        <picture>
            <source
                media={desktopMediaQuery}
                srcSet={desktopSrcSet}
            />
            {tabletProps && (
                <source
                    media={tabletMediaQuery}
                    srcSet={tabletProps.srcSet}
                    width={tabletProps.width}
                    height={tabletProps.height}
                />
            )}
            {mobileProps && (
                <source
                    srcSet={mobileProps.srcSet}
                    width={mobileProps.width}
                    height={mobileProps.height}
                />
            )}
            <img
                {...restImage}
                alt={rest.alt}
            />
        </picture>
    )
}
